// @ts-nocheck
/**
 * DeckBuilderCardGrid - デッキ構築画面用カードグリッド
 *
 * DeckBuilderCard / DeckBuilderGroupCard + 仮想スクロール (react-window) を使用
 * 大量のカードを効率的に表示
 * - grouped=false: 通常表示（レアリティ別）
 * - grouped=true: まとめ表示（カードIDでグループ化）
 */

import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { FixedSizeGrid } from 'react-window';
import { DeckBuilderCard, DeckBuilderGroupCard } from '../../components/cardV2';
import { CARD_DISPLAY_SIZES } from '../../components/cardV2/cardConstants';
import { EFFECT_LEVELS } from '../../styles/rarityEffects';

// ========================================
// 型定義
// ========================================

/** 通常モード用カードデータ */
export interface NormalCardData {
  id: string;
  name: string;
  attribute: string;
  cost: number;
  type: string;
  attack?: number;
  hp?: number;
  category?: string;
  effect?: string;
  rarity?: string;
  quantity?: number;
}

/** まとめ表示モード用カードデータ */
export interface GroupedCardData {
  card: {
    id: string;
    name: string;
    attribute: string;
    cost: number;
    type: string;
    attack?: number;
    hp?: number;
    category?: string;
    effect?: string;
  };
  cardId: string;
  rarity: string;
  rarities: Array<{ rarity: string; quantity: number }>;
  totalQuantity: number;
  canAdd: boolean;
}

export interface DeckBuilderCardGridProps {
  /** 表示するカード配列（通常モード） */
  cards?: NormalCardData[];

  /** 表示するカード配列（まとめ表示モード） */
  groupedCards?: GroupedCardData[];

  /** まとめ表示モードかどうか */
  grouped?: boolean;

  /** カードID → 使用枚数のマップ */
  usedCounts: Map<string, number>;

  /** カードクリックハンドラ（通常モード） */
  onCardClick?: (card: NormalCardData) => void;

  /** カードクリックハンドラ（まとめ表示モード） */
  onGroupedCardClick?: (cardId: string, rarities: Array<{ rarity: string; quantity: number }>) => void;

  /** カードホバーハンドラ（通常モード） */
  onCardHover?: (card: NormalCardData) => void;

  /** カードホバーハンドラ（まとめ表示モード） */
  onGroupedCardHover?: (data: GroupedCardData) => void;

  /** エフェクトレベル */
  effectLevel?: 'full' | 'minimal' | 'off';

  /** モバイル表示 */
  isMobile?: boolean;

  /** グリッドの高さ */
  height?: number;

  /** 空メッセージ */
  emptyMessage?: string;
}

// ========================================
// スタイル定義
// ========================================

const styles = {
  emptyMessage: {
    textAlign: 'center' as const,
    padding: '48px',
    color: '#a0a0a0',
    fontSize: '16px',
  },
};

// ========================================
// 仮想スクロール用の定数
// ========================================

// DeckBuilderCardの実際の高さを計算
// - カード画像: width * (4/3)
// - infoSection: padding 10px + name 12px + stats 11px = 33px
// - セル内パディング: 10px (上下5pxずつ or 初回8px)

// デスクトップ
const CARD_IMAGE_HEIGHT_DESKTOP = Math.floor(CARD_DISPLAY_SIZES.deckBuilder.width * (4 / 3));  // 120
const CARD_INFO_HEIGHT = 33;  // infoSection (通常モード)
const CARD_INFO_HEIGHT_GROUPED = 38;  // infoSection (まとめ表示: name + rarities row)
const CARD_WIDTH_DESKTOP = CARD_DISPLAY_SIZES.deckBuilder.width + 10;  // 90 + 10 gap
const CARD_HEIGHT_DESKTOP = CARD_IMAGE_HEIGHT_DESKTOP + CARD_INFO_HEIGHT + 10;  // 120 + 33 + 10 = 163
const CARD_HEIGHT_DESKTOP_GROUPED = CARD_IMAGE_HEIGHT_DESKTOP + CARD_INFO_HEIGHT_GROUPED + 10;  // 120 + 38 + 10 = 168

// モバイル
const CARD_IMAGE_HEIGHT_MOBILE = Math.floor(CARD_DISPLAY_SIZES.deckBuilderMobile.width * (4 / 3));  // 100
const CARD_INFO_HEIGHT_MOBILE = 28;  // infoSection (smaller)
const CARD_INFO_HEIGHT_MOBILE_GROUPED = 32;  // infoSection (まとめ表示)
const CARD_WIDTH_MOBILE = CARD_DISPLAY_SIZES.deckBuilderMobile.width + 8;  // 75 + 8 gap
const CARD_HEIGHT_MOBILE = CARD_IMAGE_HEIGHT_MOBILE + CARD_INFO_HEIGHT_MOBILE + 8;  // 100 + 28 + 8 = 136
const CARD_HEIGHT_MOBILE_GROUPED = CARD_IMAGE_HEIGHT_MOBILE + CARD_INFO_HEIGHT_MOBILE_GROUPED + 8;  // 100 + 32 + 8 = 140

const GRID_PADDING = 8;
const OVERSCAN_ROW_COUNT = 3;  // 画面外に3行分余分に描画（ちらつき防止）

// ========================================
// メインコンポーネント
// ========================================

const DeckBuilderCardGrid: React.FC<DeckBuilderCardGridProps> = ({
  cards = [],
  groupedCards = [],
  grouped = false,
  usedCounts,
  onCardClick,
  onGroupedCardClick,
  onCardHover,
  onGroupedCardHover,
  effectLevel = EFFECT_LEVELS.FULL,
  isMobile = false,
  height = 500,
  emptyMessage = 'カードがありません',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // コンテナ幅を監視（ResizeObserver）
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);

    // 初期幅を設定
    setContainerWidth(containerRef.current.offsetWidth);

    return () => resizeObserver.disconnect();
  }, []);

  // 使用するカード配列
  const displayCards = grouped ? groupedCards : cards;

  // グリッド計算
  const cardWidth = isMobile ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP;
  const cardHeight = isMobile
    ? (grouped ? CARD_HEIGHT_MOBILE_GROUPED : CARD_HEIGHT_MOBILE)
    : (grouped ? CARD_HEIGHT_DESKTOP_GROUPED : CARD_HEIGHT_DESKTOP);
  const gap = isMobile ? 8 : 10;

  // 列数を計算（最低1列）
  const columnCount = useMemo(() => {
    if (containerWidth <= 0) return 1;
    const availableWidth = containerWidth - GRID_PADDING * 2;
    return Math.max(1, Math.floor(availableWidth / cardWidth));
  }, [containerWidth, cardWidth]);

  // 行数を計算
  const rowCount = useMemo(() => {
    if (!displayCards || displayCards.length === 0) return 0;
    return Math.ceil(displayCards.length / columnCount);
  }, [displayCards, columnCount]);

  // セルレンダラー（通常モード）
  const NormalCell = useCallback(({ columnIndex, rowIndex, style }: {
    columnIndex: number;
    rowIndex: number;
    style: React.CSSProperties;
  }) => {
    const index = rowIndex * columnCount + columnIndex;
    if (index >= cards.length) return null;

    const card = cards[index];
    const usedCount = usedCounts.get(card.id) || 0;

    // セル内でのパディング調整
    const cellStyle: React.CSSProperties = {
      ...style,
      paddingRight: gap / 2,
      paddingBottom: gap / 2,
      paddingLeft: columnIndex === 0 ? GRID_PADDING : gap / 2,
      paddingTop: rowIndex === 0 ? GRID_PADDING : 0,
      boxSizing: 'border-box',
    };

    return (
      <div style={cellStyle}>
        <DeckBuilderCard
          card={card}
          rarity={card.rarity || 'C'}
          usedCount={usedCount}
          maxCount={3}
          effectLevel={effectLevel}
          onClick={() => onCardClick?.(card)}
          onMouseEnter={() => onCardHover?.(card)}
          isMobile={isMobile}
        />
      </div>
    );
  }, [cards, columnCount, usedCounts, onCardClick, onCardHover, effectLevel, gap, isMobile]);

  // セルレンダラー（まとめ表示モード）
  const GroupedCell = useCallback(({ columnIndex, rowIndex, style }: {
    columnIndex: number;
    rowIndex: number;
    style: React.CSSProperties;
  }) => {
    const index = rowIndex * columnCount + columnIndex;
    if (index >= groupedCards.length) return null;

    const data = groupedCards[index];
    const usedCount = usedCounts.get(data.cardId) || 0;

    // セル内でのパディング調整
    const cellStyle: React.CSSProperties = {
      ...style,
      paddingRight: gap / 2,
      paddingBottom: gap / 2,
      paddingLeft: columnIndex === 0 ? GRID_PADDING : gap / 2,
      paddingTop: rowIndex === 0 ? GRID_PADDING : 0,
      boxSizing: 'border-box',
    };

    return (
      <div style={cellStyle}>
        <DeckBuilderGroupCard
          card={data.card}
          rarity={data.rarity}
          rarities={data.rarities}
          usedCount={usedCount}
          maxCount={3}
          canAdd={data.canAdd}
          effectLevel={effectLevel}
          onClick={() => onGroupedCardClick?.(data.cardId, data.rarities)}
          onMouseEnter={() => onGroupedCardHover?.(data)}
          isMobile={isMobile}
        />
      </div>
    );
  }, [groupedCards, columnCount, usedCounts, onGroupedCardClick, onGroupedCardHover, effectLevel, gap, isMobile]);

  // 空の場合
  if (!displayCards || displayCards.length === 0) {
    return (
      <div style={styles.emptyMessage}>
        {emptyMessage}
      </div>
    );
  }

  // コンテナ幅が未確定の場合はローディング表示
  if (containerWidth <= 0) {
    return (
      <div ref={containerRef} style={{ width: '100%', height, minHeight: 200 }}>
        <div style={styles.emptyMessage}>読み込み中...</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <FixedSizeGrid
        columnCount={columnCount}
        columnWidth={cardWidth}
        height={height}
        rowCount={rowCount}
        rowHeight={cardHeight}
        width={containerWidth}
        overscanRowCount={OVERSCAN_ROW_COUNT}
        style={{
          overflowX: 'hidden',
        }}
      >
        {grouped ? GroupedCell : NormalCell}
      </FixedSizeGrid>
    </div>
  );
};

export default DeckBuilderCardGrid;
